import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Download, CheckCircle, MapPin, Phone, Mail, Globe, ArrowRight } from "lucide-react";
import { PARTNER_DETTAGLI } from "@/data/partnerConvenzioniData";

export default function ConvenzionePartnerPage() {
  const { slug } = useParams();
  const partner = PARTNER_DETTAGLI[slug];

  if (!partner) {
    return (
      <main id="main-content" className="py-20 text-center">
        <h1 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-4">Convenzione non trovata</h1>
        <Link to="/convenzioni" className="text-[var(--confcom-blue)] font-medium hover:underline">Torna alle convenzioni</Link>
      </main>
    );
  }

  return (
    <main id="main-content">

      {/* ── HERO ── */}
      <section className="relative h-[280px] md:h-[360px] overflow-hidden">
        {partner.heroPhoto ? (
          <>
            <img src={partner.heroPhoto} alt={partner.nome} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ backgroundColor: "rgba(4,30,66,0.85)" }} />
          </>
        ) : (
          <div className="absolute inset-0 bg-[var(--confcom-blue)]" />
        )}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative h-full flex flex-col justify-end pb-12">
          <Link
            to="/convenzioni"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white mb-4 transition-colors w-fit"
            data-testid="partner-back-link"
          >
            <ArrowLeft size={16} /> Tutte le convenzioni
          </Link>
          <div className="flex items-start gap-6">
            {partner.image && (
              <div className="hidden sm:flex shrink-0 w-20 h-20 rounded-xl overflow-hidden items-center justify-center p-2 shadow-lg"
                style={{ backgroundColor: partner.logoBg || "#fff" }}>
                <img src={partner.image} alt={partner.nome} className="w-full h-full object-contain" />
              </div>
            )}
            <div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight">
                {partner.nome}
              </h1>
              {partner.tagline && (
                <p className="mt-2 text-white/80 text-lg max-w-2xl">{partner.tagline}</p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── SCONTO BANNER ── */}
      {partner.scontoLabel && (
        <div className="bg-[var(--confcom-blue)] border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-white/90 text-sm sm:text-base max-w-2xl leading-relaxed">
              {partner.scontoBadge}
            </p>
            <div className="shrink-0 text-right">
              <span className="text-4xl lg:text-5xl font-black text-[#56B7E6] font-['Encode_Sans_Semi_Condensed'] tracking-tight leading-none">
                {partner.scontoLabel}
              </span>
              <p className="text-white/60 text-xs mt-1 uppercase tracking-widest">Risparmio associati</p>
            </div>
          </div>
        </div>
      )}

      {/* ── INTRO ── */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-10 items-start">
            {/* Logo + PDF CTA */}
            <div className="lg:col-span-1 flex flex-col items-center gap-5">
              {partner.image && (
                <div className="w-full max-w-xs rounded-2xl overflow-hidden p-8 shadow-sm border border-[var(--confcom-border)] flex items-center justify-center"
                  style={{ backgroundColor: partner.logoBg || "#fff" }}>
                  <img src={partner.image} alt={partner.nome} className="max-h-28 w-auto object-contain" />
                </div>
              )}
              {partner.pdf && (
                <a
                  href={partner.pdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full max-w-xs flex items-center justify-center gap-2 bg-[var(--confcom-blue)] text-white px-6 py-3 rounded-md font-semibold hover:opacity-90 transition-opacity text-sm"
                  data-testid="partner-pdf-download"
                >
                  <Download size={16} /> Scarica la scheda (PDF)
                </a>
              )}
              {partner.sitoWeb && (
                <a
                  href={partner.sitoWeb}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full max-w-xs flex items-center justify-center gap-2 border border-[var(--confcom-blue)] text-[var(--confcom-blue)] px-6 py-3 rounded-md font-semibold hover:opacity-90 transition-opacity text-sm"
                >
                  <Globe size={16} /> Visita il sito web
                </a>
              )}
            </div>

            {/* Description */}
            <div className="lg:col-span-2">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">
                La convenzione
              </span>
              <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mt-2 mb-5">
                {partner.tagline || partner.nome}
              </h2>
              {partner.descrizione && partner.descrizione.split("\n\n").map((para, i) => (
                <p key={i} className="text-[var(--confcom-text-secondary)] leading-relaxed text-base mb-4">{para}</p>
              ))}
              {partner.dettaglio && (
                <p className="text-[var(--confcom-text-secondary)] leading-relaxed text-base">{partner.dettaglio}</p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── HIGHLIGHTS (NTM) ── */}
      {partner.highlights && (
        <section className="py-10 bg-[var(--confcom-blue)]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {partner.highlights.map((h) => (
                <div key={h.label} className="text-center">
                  <p className="text-4xl font-black text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">{h.valore}</p>
                  <p className="text-white/80 text-sm mt-1">{h.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── SERVIZI (NTM) ── */}
      {partner.servizi && (
        <section className="py-14 bg-[#EBF5FF]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">
              I nostri servizi
            </span>
            <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mt-2 mb-8">
              Quattro soluzioni per ogni esigenza di spedizione
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {partner.servizi.map((s, i) => (
                <div key={i} className="bg-white rounded-xl p-6 border border-[var(--confcom-border)] hover:shadow-md transition-shadow">
                  <div className="w-10 h-10 bg-[var(--confcom-blue)] rounded-lg flex items-center justify-center mb-4">
                    <span className="text-white font-bold text-sm">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <h3 className="text-base font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-3">
                    {s.titolo}
                  </h3>
                  <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── VANTAGGI (Peugeot ecc.) ── */}
      {partner.vantaggi && (
        <section className="py-14 bg-[#EBF5FF]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">
              Vantaggi esclusivi
            </span>
            <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mt-2 mb-8">
              I vantaggi per gli associati Confcommercio Roma
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {partner.vantaggi.map((v, i) => (
                <div key={i} className="flex items-start gap-4 bg-white rounded-xl p-5 border border-[var(--confcom-border)]">
                  <CheckCircle size={20} className="text-[var(--confcom-blue)] shrink-0 mt-0.5" />
                  <span className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">{v}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CONCESSIONARI (Peugeot) ── */}
      {partner.concessionari && (
        <section className="py-14 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">
              Dove acquistare
            </span>
            <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mt-2 mb-8">
              Scegli un nostro concessionario associato
            </h2>
            <div className="grid sm:grid-cols-3 gap-6">
              {partner.concessionari.map((c, i) => (
                <div key={i} className="bg-[#EBF5FF] rounded-xl p-6 border border-[var(--confcom-border)]">
                  <div className="w-10 h-10 bg-[var(--confcom-blue)] rounded-lg flex items-center justify-center mb-4">
                    <MapPin size={18} className="text-white" />
                  </div>
                  <h3 className="text-base font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-2">
                    {c.nome}
                  </h3>
                  <p className="text-sm text-[var(--confcom-text-secondary)]">{c.indirizzo}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── ABBONAMENTI (SABA) ── */}
      {partner.abbonamenti && (
        <section className="py-14 bg-[#EBF5FF]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">
              Tre tipologie disponibili
            </span>
            <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mt-2 mb-8">
              Scegli il tuo abbonamento mensile
            </h2>
            <div className="grid sm:grid-cols-3 gap-6">
              {partner.abbonamenti.map((a, i) => (
                <div key={i} className="bg-white rounded-xl p-8 border-2 border-[var(--confcom-border)] hover:border-[var(--confcom-blue)]/40 hover:shadow-md transition-all text-center">
                  <div className="w-14 h-14 bg-[var(--confcom-blue)] rounded-full flex items-center justify-center mx-auto mb-5">
                    <span className="text-[#56B7E6] font-black text-lg">{i + 1}</span>
                  </div>
                  <h3 className="text-lg font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-3">
                    {a.tipo}
                  </h3>
                  <p className="text-sm text-[var(--confcom-text-secondary)]">{a.orari}</p>
                  <div className="mt-4 pt-4 border-t border-[var(--confcom-border)]">
                    <span className="text-xs font-bold text-[#56B7E6] uppercase tracking-wider">Sconto 20-30%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── PRODOTTI (Generali) ── */}
      {partner.prodotti && (
        <section className="py-14 bg-[#EBF5FF]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-8">
              Prodotti dedicati agli associati
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {partner.prodotti.map((p) => (
                <div key={p.nome} className="bg-white rounded-xl p-6 border border-[var(--confcom-border)]">
                  <h3 className="text-base font-bold text-[var(--confcom-blue)] font-['Encode_Sans_Semi_Condensed'] mb-2">{p.nome}</h3>
                  <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CONDIZIONI (FCA) ── */}
      {partner.condizioni && (
        <section className="py-14 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">
              Note legali
            </span>
            <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mt-2 mb-8">
              Condizioni generali
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {partner.condizioni.map((c, i) => (
                <div key={i} className="flex items-start gap-3 p-5 bg-[#EBF5FF] rounded-xl">
                  <span className="w-2 h-2 rounded-full bg-[var(--confcom-blue)] mt-2 shrink-0" />
                  <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">{c}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}


      {/* ── COME USUFRUIRE ── */}
      {partner.come && (
        <section className="py-14 bg-[var(--confcom-blue)]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">
                  Come funziona
                </span>
                <h2 className="text-2xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] mt-2 mb-6">
                  Come usufruire dell'accordo
                </h2>
                <p className="text-white/80 leading-relaxed text-base">{partner.come}</p>

                {partner.indirizzo && (
                  <div className="mt-6 flex items-start gap-3 text-white/80">
                    <MapPin size={16} className="shrink-0 mt-0.5 text-[#56B7E6]" />
                    <span className="text-sm">{partner.indirizzo}</span>
                  </div>
                )}
              </div>
              <div className="space-y-4">
                <div className="bg-white/10 rounded-xl p-6 border border-white/10">
                  <h3 className="text-base font-bold text-white font-['Encode_Sans_Semi_Condensed'] mb-4">
                    Per maggiori informazioni
                  </h3>
                  {partner.emailPartner && (
                    <a href={`mailto:${partner.emailPartner}`} className="flex items-center gap-3 text-sm text-white/80 hover:text-white mb-3 transition-colors">
                      <Mail size={15} className="text-[#56B7E6] shrink-0" />
                      {partner.emailPartner}
                    </a>
                  )}
                  {partner.telefonoConfcom && (
                    <a href={`tel:${partner.telefonoConfcom}`} className="flex items-center gap-3 text-sm text-white/80 hover:text-white mb-3 transition-colors">
                      <Phone size={15} className="text-[#56B7E6] shrink-0" />
                      Confcommercio Roma: {partner.telefonoConfcom}
                    </a>
                  )}
                  {(partner.emailConfcom || partner.contatti?.includes("@confcommercioroma")) && (
                    <a href="mailto:associati@confcommercioroma.it" className="flex items-center gap-3 text-sm text-white/80 hover:text-white transition-colors">
                      <Mail size={15} className="text-[#56B7E6] shrink-0" />
                      associati@confcommercioroma.it
                    </a>
                  )}
                  {partner.contatti && !partner.contatti.includes("@confcommercioroma") && (
                    <p className="text-sm text-white/80 mt-2">{partner.contatti}</p>
                  )}
                </div>
                {partner.pdf && (
                  <a
                    href={partner.pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-[#56B7E6] text-[var(--confcom-blue)] px-6 py-4 rounded-xl font-bold hover:opacity-90 transition-opacity w-full"
                  >
                    <Download size={18} /> Scarica la scheda informativa
                  </a>
                )}
                <Link
                  to={`/convenzioni/${partner.categoriaSlug}`}
                  className="flex items-center justify-center gap-2 border-2 border-white/40 text-white px-6 py-3 rounded-md font-medium hover:opacity-90 transition-opacity w-full text-sm"
                >
                  <ArrowLeft size={16} /> Torna alla categoria
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

    </main>
  );
}
